class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.setupMobileDragToClose();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    removeTrapFocus();

    if (this.activeElement) {
      try {
        this.activeElement.focus({ preventScroll: true });
      } catch (error) {
        this.activeElement.focus();
      }
    }

    document.body.classList.remove('overflow-hidden');
  }

  setupMobileDragToClose() {
    let startX = 0;
    let startY = 0;
    let currentX = 0;

    let dragging = false;
    let horizontalDrag = false;

    const CLOSE_RATIO = 0.45;

    const getDrawerInner = () =>
      this.querySelector('.drawer__inner');

    const resetDrawer = () => {
      const drawerInner = getDrawerInner();
      if (!drawerInner) return;

      drawerInner.style.transition = 'transform 0.22s ease';
      drawerInner.style.transform = 'translateX(0)';

      setTimeout(() => {
        drawerInner.style.transition = '';
        drawerInner.style.transform = '';
      }, 220);
    };

    this.addEventListener(
      'touchstart',
      (event) => {
        if (window.innerWidth > 749) return;

        const drawerInner = getDrawerInner();

        if (
          !drawerInner ||
          !drawerInner.contains(event.target)
        ) {
          return;
        }

        const touch = event.touches[0];

        startX = touch.clientX;
        startY = touch.clientY;
        currentX = 0;

        dragging = true;
        horizontalDrag = false;

        drawerInner.style.transition = 'none';
      },
      { passive: true }
    );

    this.addEventListener(
      'touchmove',
      (event) => {
        if (!dragging || window.innerWidth > 749) {
          return;
        }

        const drawerInner = getDrawerInner();
        if (!drawerInner) return;

        const touch = event.touches[0];

        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;

        /*
        * First determine whether the user wants
        * to scroll vertically or drag horizontally.
        */
        if (!horizontalDrag) {
          if (
            Math.abs(deltaY) >
            Math.abs(deltaX)
          ) {
            dragging = false;

            drawerInner.style.transition = '';
            drawerInner.style.transform = '';

            return;
          }

          if (Math.abs(deltaX) > 6) {
            horizontalDrag = true;
          }
        }

        if (!horizontalDrag) return;

        /*
        * Once horizontal dragging is confirmed,
        * stop the page/cart from vertically moving.
        */
        event.preventDefault();

        const drawerWidth =
          drawerInner.getBoundingClientRect().width;

        /*
        * Rightward movement only.
        */
        currentX = Math.max(0, deltaX);

        /*
        * Never move farther than drawer width.
        */
        currentX = Math.min(
          currentX,
          drawerWidth
        );

        /*
        * Drawer follows finger 1:1.
        */
        drawerInner.style.transform =
          `translateX(${currentX}px)`;
      },
      { passive: false }
    );

    this.addEventListener(
      'touchend',
      () => {
        if (!dragging || !horizontalDrag) {
          dragging = false;
          horizontalDrag = false;
          return;
        }

        const drawerInner = getDrawerInner();
        if (!drawerInner) return;

        const drawerWidth =
          drawerInner.getBoundingClientRect().width;

        const progress =
          currentX / drawerWidth;

        dragging = false;
        horizontalDrag = false;

        /*
        * Only finish closing if the user has
        * dragged almost the entire drawer away.
        */
        if (progress >= CLOSE_RATIO) {
          drawerInner.style.transition =
            'transform 0.18s ease';

          drawerInner.style.transform =
            `translateX(${drawerWidth}px)`;

          setTimeout(() => {
            this.close();

            drawerInner.style.transition = '';
            drawerInner.style.transform = '';
          }, 180);

          return;
        }

        /*
        * Finger released too early:
        * smoothly return the drawer.
        */
        resetDrawer();
      }
    );

    this.addEventListener(
      'touchcancel',
      () => {
        if (!dragging) return;

        dragging = false;
        horizontalDrag = false;

        resetDrawer();
      }
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
